import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';

/**
 * Reverse-geocode coordinates → a human-readable street address. Goes through the app's own backend
 * (`GET /geo/reverse`), which reaches OpenStreetMap Nominatim server-side and caches the result — so it
 * works even when the client's network blocks Nominatim, and respects its usage policy. Returns null on
 * any failure so callers can fall back to a plain map link.
 */
export async function reverseGeocode(lat: number | string, lng: number | string): Promise<string | null> {
  try {
    const r = await request<{ address: string | null }>({ url: '/geo/reverse', params: { lat, lng } });
    return r?.address ?? null;
  } catch {
    return null;
  }
}

/** Cached reverse-geocode hook — coords→address never changes, so cache forever + never retry. */
export function useReverseGeocode(lat?: number | string | null, lng?: number | string | null) {
  const enabled = lat != null && lng != null && String(lat).trim() !== '' && String(lng).trim() !== '';
  return useQuery({
    queryKey: ['reverse-geocode', String(lat), String(lng)],
    queryFn: () => reverseGeocode(lat!, lng!),
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}
